import { FormEvent, useState } from "react";
import { ArrowRight, Building2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { managementServiceImage } from "@/data/site";
import { SERVICE_LINES } from "@shared/serviceLines";
import { trpc } from "@/lib/trpc";

/**
 * Client registration — the front door of an engagement.
 *
 * Submitting this grants nothing. It opens a prospective engagement for
 * Altitude to review; approval is what seeds the plan and issues portal
 * access. The copy says so plainly, because a form that looks like it creates
 * an account and then does not is worse than one that sets expectations.
 */
export default function ClientRegister() {
  const [done, setDone] = useState<string | null>(null);

  const register = trpc.clientPortal.register.useMutation({
    onSuccess: result => setDone(result.reference ?? "received"),
    onError: error => toast.error("That did not send", { description: error.message }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const optional = (key: string) => {
      const value = String(data.get(key) ?? "").trim();
      return value ? { [key]: value } : {};
    };
    register.mutate({
      companyName: String(data.get("companyName") ?? "").trim(),
      serviceLine: String(data.get("serviceLine") ?? "general"),
      contactName: String(data.get("contactName") ?? "").trim(),
      contactEmail: String(data.get("contactEmail") ?? "").trim(),
      ...optional("legalEntity"),
      ...optional("addressLine1"),
      ...optional("addressLine2"),
      ...optional("city"),
      ...optional("state"),
      ...optional("postalCode"),
      ...optional("phone"),
      ...optional("website"),
      ...optional("contactTitle"),
      ...optional("projectSummary"),
    });
  }

  if (done) {
    return (
      <SiteLayout>
        <div className="cp-page cp-page--narrow">
          <section className="section">
            <div className="site-container cp-auth">
              <span className="cp-auth__tick"><CheckCircle2 size={28} /></span>
              <p className="eyebrow">Registration received</p>
              <h1>Thank you.</h1>
              {done !== "received" && (
                <p className="cp-reference">Your reference is <strong>{done}</strong></p>
              )}
              <p>
                An Altitude engagement lead will review your registration and come back to you
                directly. Once the engagement is opened we will issue portal access to the email
                address you gave us, along with the documents for signature.
              </p>
              <p className="cp-auth__fineprint">
                Nothing is billed at this stage and no agreement is in place until documents are
                executed.
              </p>
            </div>
          </section>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="cp-page">
        <section className="cp-hero cp-hero--photo">
          {/* The photograph sits behind the gradient that was already here,
              rather than replacing it. The gradient is what carries the text
              contrast, so keeping it means the headline stays legible whatever
              the image does underneath. Decorative, so it is hidden from
              assistive tech. */}
          <div className="cp-hero__image" style={{ backgroundImage: `url(${managementServiceImage})` }} aria-hidden="true" />
          <div className="site-container">
            <p className="eyebrow eyebrow--light">Client registration</p>
            <h1>Begin your<br /><em>engagement.</em></h1>
            <p>
              Tell us about the facility and the work. We review every registration personally,
              and if it is a fit we will open the engagement and issue your portal access.
            </p>
          </div>
        </section>

        <section className="section">
          <div className="site-container cp-register">
            <aside className="cp-register__aside">
              <div className="cp-step"><span>1</span><div><strong>Register</strong><p>This form. A few minutes.</p></div></div>
              <div className="cp-step"><span>2</span><div><strong>Review</strong><p>We assess fit and scope, and come back to you.</p></div></div>
              <div className="cp-step"><span>3</span><div><strong>Documents</strong><p>Agreement, NDA and POA issued for signature.</p></div></div>
              <div className="cp-step"><span>4</span><div><strong>Autopay</strong><p>A verified payment method on file before services begin.</p></div></div>
              <div className="cp-step"><span>5</span><div><strong>Portal access</strong><p>Your workspace opens with the full engagement plan.</p></div></div>
              <div className="cp-assure">
                <ShieldCheck size={17} />
                <p>Your portal is protected by a sign-in link and an authenticator app. We never ask for a password.</p>
              </div>
            </aside>

            <form onSubmit={onSubmit} className="cp-form">
              <fieldset>
                <legend><Building2 size={14} />The organisation</legend>
                <label className="field">
                  <span>Facility or company name *</span>
                  <input name="companyName" required maxLength={200} />
                </label>
                <label className="field">
                  <span>Legal entity</span>
                  <input name="legalEntity" maxLength={200} placeholder="If different from the trading name" />
                </label>
                <label className="field">
                  <span>Street address</span>
                  <input name="addressLine1" maxLength={200} />
                </label>
                <label className="field">
                  <span>Suite, floor or unit</span>
                  <input name="addressLine2" maxLength={200} />
                </label>
                <div className="cp-grid-3">
                  <label className="field"><span>City</span><input name="city" maxLength={120} /></label>
                  <label className="field"><span>State</span><input name="state" maxLength={60} /></label>
                  <label className="field"><span>ZIP</span><input name="postalCode" maxLength={24} /></label>
                </div>
                <div className="cp-grid-2">
                  <label className="field"><span>Phone</span><input name="phone" type="tel" maxLength={40} /></label>
                  <label className="field"><span>Website</span><input name="website" type="url" placeholder="https://" maxLength={400} /></label>
                </div>
              </fieldset>

              <fieldset>
                <legend>What you need</legend>
                <label className="field">
                  <span>What you need *</span>
                  <select name="serviceLine" required defaultValue="general">
                    {SERVICE_LINES.map(line => (
                      <option key={line.value} value={line.value}>{line.label}</option>
                    ))}
                  </select>
                </label>
                {/* The hints, rather than leaving someone to guess the
                    difference between a build-out and operations consulting.
                    Rendered as a list so it reads at a glance and does not
                    depend on hovering anything. */}
                <ul className="cp-service-hints">
                  {SERVICE_LINES.map(line => (
                    <li key={line.value}><strong>{line.label}</strong><span>{line.hint}</span></li>
                  ))}
                </ul>
                <label className="field">
                  <span>Tell us about the project</span>
                  <textarea
                    name="projectSummary"
                    rows={6}
                    maxLength={6000}
                    placeholder="Where the facility is today, what you are trying to achieve, and any timing that matters."
                  />
                </label>
              </fieldset>

              <fieldset>
                <legend>Primary contact</legend>
                <div className="cp-grid-2">
                  <label className="field"><span>Full name *</span><input name="contactName" required maxLength={160} /></label>
                  <label className="field"><span>Title</span><input name="contactTitle" maxLength={120} /></label>
                </div>
                <label className="field">
                  <span>Email address *</span>
                  <input name="contactEmail" type="email" inputMode="email" autoComplete="email" required maxLength={320} />
                  <small className="cp-hint">
                    This becomes the owner account on the portal, so use an address you control.
                  </small>
                </label>
              </fieldset>

              <button className="button button--coral button--full" disabled={register.isPending}>
                {register.isPending ? "Sending…" : <>Submit registration<ArrowRight size={16} /></>}
              </button>
              <p className="cp-form__note">
                Submitting this does not create an agreement or a charge. Portal access is issued
                after we review and open the engagement.
              </p>
              <a className="cp-form__link" href="/clients/login">Already a client? Sign in</a>
            </form>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
